import type { FC, ReactNode } from 'react'

import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import SectionHeading from '@/components/landing/section-heading'
import { ACCENT_STYLES, MODES } from '@/constants/landing'

const ModesSection: FC = (): ReactNode => {
    return (
        <section id='modes' aria-labelledby='modes-title' className='relative scroll-mt-20 overflow-hidden py-24 sm:py-32'>
            <div aria-hidden='true' className='neon-grid-bg pointer-events-none absolute inset-0 -z-10 opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]' />

            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    id='modes-title'
                    eyebrow='Time controls'
                    accent='violet'
                    align='center'
                    title='Pick your pace.'
                    description="From one-minute bullet to classical games you can sink an evening into. Every clock is rated separately, so your blitz form never drags down your rapid."
                />

                <ul className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                    {MODES.map(mode => {
                        const accent = ACCENT_STYLES[mode.accent]
                        const Icon = mode.icon

                        return (
                            <li
                                key={mode.name}
                                className={cn(
                                    'group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a19] p-6 transition-all duration-300 hover:-translate-y-1',
                                    accent.hoverBorder
                                )}
                            >
                                <div
                                    aria-hidden='true'
                                    className='pointer-events-none absolute inset-x-0 top-0 h-px opacity-70'
                                    style={{ background: `linear-gradient(90deg, transparent, ${accent.hex}, transparent)` }}
                                />

                                <p className={cn('inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.22em]', accent.text)}>
                                    <Icon className='size-4' />
                                    {mode.name}
                                </p>

                                <p
                                    className={cn('mt-8 font-heading text-5xl font-semibold tracking-tight', accent.text)}
                                    style={{ textShadow: `0 0 22px ${accent.hex}80` }}
                                >
                                    {mode.clock}
                                </p>

                                <p className='mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground'>
                                    Minutes + seconds per move
                                </p>

                                <p className='mt-6 text-sm leading-relaxed text-muted-foreground'>
                                    {mode.description}
                                </p>

                                <p className='mt-auto flex items-center gap-2 pt-6 text-xs font-medium text-foreground/75'>
                                    <Clock className='size-4 text-muted-foreground' />
                                    {mode.detail}
                                </p>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default ModesSection
